/* eslint-disable */
import * as Immutable from 'immutable';
import throwEntityCouldNotAddEntityError from '../../Errors/Entity/EntityCouldNotAddEntity.error';
import throwEntityCouldNotDeselectEntityError from '../../Errors/Entity/EntityCouldNotDeselectEntity.error';
import throwEntityCouldNotRemoveEntityError from '../../Errors/Entity/EntityCouldNotRemoveEntity.error';
import throwEntityCouldNotSelectEntityError from '../../Errors/Entity/EntityCouldNotSelectEntity.error';
import throwEntityCouldNotUpdateEntityError from '../../Errors/Entity/EntityCouldNotUpdateEntity.error';
import './Entity.service.i18n';

/**
 * Create a store for entities
 * @type {EntityServiceDataFunction<any>}
 * @returns {EntityServiceStore} The entity store
 */
const createEntityStore = () => {
  return Immutable.Map({
    entities: Immutable.Map({}),
    ids: Immutable.List([]),
    loading: true,
    selectedIds: Immutable.List([]),
  });
};

/**
 * EntityStorageService
 * @class EntityStorageService
 */
class EntityStorageService {
  /**
   * The property to uniquely identify each entity
   * @type {string}
   * @memberof EntityStorageService
   */
  id;

  /**
   * The store where entities will be managed
   * @type {EntityServiceStore}
   * @memberof EntityStorageService
   */
  store;

  /**
   * Creates an instance of `EntityStorageService`
   * @param {EntityStorageServiceCreateDto} dto The params required to create an instance of `EntityStorageService`
   * @memberof EntityStorageService
   */
  constructor({ id = `id`, store = createEntityStore() }) {
    this.id = id;
    this.store = store;
  }

  /**
   * Add the given `entities` to the store. Removing any that may already be in the store.
   * @param {Array<Entity>} entities The `entities` to add
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  addAll = (entities) => {
    this.removeAll();
    this.addMany(entities);
    return this;
  };

  /**
   * Add the given `entities` to the store
   * @param {Array<Entity>} entities The `entities` to add
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  addMany = (entities) => {
    entities.forEach((entity) => {
      this.addOne(entity);
    });
    return this;
  };

  /**
   * Add the given `entity` to the store
   * @param {Entity} entity The `entity` to add
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  addOne = (entity) => {
    if (this.store.hasIn([`entities`, entity[this.id]])) {
      return throwEntityCouldNotAddEntityError(entity[this.id]);
    }
    this.store = this.store.setIn([`entities`, entity[this.id]], entity);
    this.store = this.store.set(`ids`, this.store.get(`ids`).push(entity[this.id]));
    return this;
  };

  /**
   * Deselect all of the currently selected entities
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  deselectAll = () => {
    this.store = this.store.set(`selectedIds`, Immutable.List([]));
    return this;
  };

  /**
   * Deselect the `entities` that matching the given `entityIds`
   * @param {Array<string>} entityIds The `id`'s of the `entities` to deselect
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  deselectMany = (entityIds) => {
    entityIds.forEach((entityId) => {
      this.deselectOne(entityId);
    });
    return this;
  };

  /**
   * Deselect the `entity` that matches the given `entityId`
   * @param {string} entityId The `id` of the `entity` to deselect
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  deselectOne = (entityId) => {
    if (!this.store.hasIn([`entities`, entityId])) {
      return throwEntityCouldNotDeselectEntityError(entityId);
    }
    if (!this.store.get(`selectedIds`).includes(entityId)) {
      return this;
    }
    this.store = this.store.set(`selectedIds`, this.store.get(`selectedIds`).filterNot((id) => id === entityId));
    return this;
  };

  /**
   * Flag the `entity` collection as loaded
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  loaded = () => {
    this.store = this.store.set(`loading`, false);
    return this;
  };

  /**
   * Flag the `entity` collection as still loading
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  loading = () => {
    this.store = this.store.set(`loading`, true);
    return this;
  };

  /**
   * Get the JavaScript representation of the store
   * @returns {EntityServiceData<any>} The current data within the store
   * @memberof EntityStorageService
   */
  get = () => {
    return this.store.toJS();
  };

  /**
   * Remove all `entities` from the store
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  removeAll = () => {
    this.store = this.store.set(`entities`, Immutable.Map({}));
    this.store = this.store.set(`ids`, Immutable.List([]));
    this.store = this.store.set(`selectedIds`, Immutable.List([]));
    return this;
  };

  /**
   * Remove all `entities` that matching the given `entityIds`
   * @param {Array<string>} entityIds The `id`'s of the entities to remove from the store
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  removeMany = (entityIds) => {
    this.store = this.store.set(`entities`, this.store.get(`entities`).removeAll(entityIds));
    this.store = this.store.set(`ids`, this.store.get(`ids`).filterNot((id) => entityIds.includes(id)));
    this.store = this.store.set(`selectedIds`, this.store.get(`selectedIds`).filterNot((id) => entityIds.includes(id)));
    return this;
  };

  /**
   * Remove the given `entity` from the store
   * @param {string} entityId The `id` of the `entity` to remove
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  removeOne = (entityId) => {
    if (!this.store.hasIn([`entities`, entityId])) {
      return throwEntityCouldNotRemoveEntityError(entityId);
    }
    this.store = this.store.set(`entities`, this.store.get(`entities`).remove(entityId));
    this.store = this.store.set(`ids`, this.store.get(`ids`).filterNot((id) => id === entityId));
    this.store = this.store.set(`selectedIds`, this.store.get(`selectedIds`).filterNot((id) => id === entityId));
    return this;
  };

  /**
   * Completely blow away the store and reset it back to its initial state
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  reset = () => {
    this.store = createEntityStore();
    return this;
  };

  /**
   * Select all of the `entities` currently withing the store
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  selectAll = () => {
    this.store = this.store.set(`selectedIds`, this.store.get(`ids`));
    return this;
  };

  /**
   * Select the `entities` that matching the given `entityIds`
   * @param {Array<string>} entityIds The `id`'s of the `entities` to select
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  selectMany = (entityIds) => {
    entityIds.forEach((entityId) => {
      this.selectOne(entityId);
    });
    return this;
  };

  /**
   * Select the `entity` that matches the given `entityId`
   * @param {string} entityId The `id` of the `entity` to select
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  selectOne = (entityId) => {
    if (!this.store.hasIn([`entities`, entityId])) {
      return throwEntityCouldNotSelectEntityError(entityId);
    }
    if (this.store.get(`selectedIds`).includes(entityId)) {
      return this;
    }
    this.store = this.store.set(`selectedIds`, this.store.get(`selectedIds`).push(entityId));
    return this;
  };

  /**
   * Update the given `entities`
   * @param {Array<{id: string, changes: any}>} updates The `entities` to update
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  updateMany = (updates) => {
    updates.forEach(({ id, changes }) => {
      this.updateOne(id, changes);
    });
    return this;
  };

  /**
   * Update the given `entity`
   * @param {string} id The `id` of the `entity` to update
   * @param {any} changes The changes to apply
   * @returns {this} The current `EntityStorageService` instance
   * @memberof EntityStorageService
   */
  updateOne = (id, changes) => {
    if (!this.store.hasIn([`entities`, id])) {
      return throwEntityCouldNotUpdateEntityError(id);
    }
    this.store = this.store.mergeIn([`entities`, id], changes);
    return this;
  };
}

/**
 * EntityService
 * @class EntityService
 */
class EntityService {
  /**
   * Hydrate an instance of `EntityStorageService` with a default entity store
   * @param {string} [id=`id`] The default property to reference `id`'s on the `entities`
   * @returns {EntityStorageService} The storage class to use
   * @memberof EntityService
   */
  create = (id = `id`) => {
    const entityStorageService = new EntityStorageService({ id, store: createEntityStore() });
    return entityStorageService;
  };

  /**
   * Hydrate an instance of `EntityStorageService` from a given `obj`
   * @param {any} state The state to use for hydration
   * @param {string} [id=`id`] The default property to reference `id`'s on the `entities`
   * @returns {EntityStorageService} The storage class to use
   * @memberof EntityService
   */
  from = (state, id = `id`) => {
    const store = Immutable.Map.isMap(state) ? state : Immutable.fromJS(state);
    const entityStorageService = new EntityStorageService({ id, store });
    return entityStorageService;
  };

  /**
   * Hydrate an instance of `EntityStorageService` from a given `promise`
   * @param {Promise<any>} promise The promise to resolve and use for hydration
   * @param {string} [id=`id`] The default property to reference `id`'s on the `entities`
   * @returns {Promise<EntityStorageService>} The storage class to use
   * @memberof EntityService
   */
  fromPromise = async (promise, id = `id`) => {
    return promise.then((value) => {
      return this.from(value, id);
    });
  };
}

export default EntityService;
